import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { Alert, Grid, Skeleton, Stack } from "@mui/material";
import SectionWrapper from "../../../Widgets/SectionWrapper";
import DataTable, { DataTableColumn, DataTableRow } from "../../../Widgets/DataTable";
import LabeledSelect from "../../../Widgets/LabeledSelect";
import LabeledText from "../../../Widgets/LabeledText";
import RowOptions from "../../../Widgets/RowOptions";
import ConfirmDialog from "../../GoalsAndTargets/shared/ConfirmDialog";
import ConcernEntryModal from "./ConcernEntryModal";
import {
  AreaOfConcernEntry,
  ConcernCategoryKey,
  CONCERN_CATEGORIES_CONFIG,
  ENUM_TO_CATEGORY,
  SAFETY_CONCERNS_OPTIONS,
} from "./interface";
import useGetAreasOfConcern from "hooks/api/client/useGetAreasOfConcern";
import useCreateAreaOfConcern from "hooks/api/client/useCreateAreaOfConcern";
import useUpdateAreaOfConcern from "hooks/api/client/useUpdateAreaOfConcern";
import useDeleteAreaOfConcern from "hooks/api/client/useDeleteAreaOfConcern";
import { useGetClientById } from "hooks/api/useGetClientById";
import { useUpdateClient } from "hooks/api/client/useUpdateClient";
import {
  getUserId,
  isAdmin,
  isClinician,
  isDirector,
  isSuperAdmin,
  isSupervisor,
} from "selectors";

const BASE_COLUMNS: DataTableColumn[] = [
  { key: "reportedDate", label: "Date", width: 120 },
  { key: "reportedByName", label: "Reported By", width: 160 },
];

const ACTIONS_COLUMN: DataTableColumn = { key: "actions", label: "", width: 56 };

const getColumns = (categoryLabel: string, showActions: boolean): DataTableColumn[] => [
  { key: "concernEntry", label: categoryLabel },
  ...BASE_COLUMNS,
  ...(showActions ? [ACTIONS_COLUMN] : []),
];

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(dateWithoutTimezone(value), "yyyy-MM-dd");
  } catch {
    return "-";
  }
};

interface ModalState {
  open: boolean;
  categoryKey: ConcernCategoryKey | null;
  editingEntry: AreaOfConcernEntry | null;
}

const CLOSED_MODAL: ModalState = { open: false, categoryKey: null, editingEntry: null };

export const AreasOfConcern: React.FC = () => {
  const { t } = useTranslation(["clientDetails"]);
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { areasOfConcern, refetchAreasOfConcern, isFetched: isAreasOfConcernFetched } =
    useGetAreasOfConcern(clientIdNum);
  const { data: clientData, refetch: refetchClient, isFetched: isClientFetched } =
    useGetClientById(clientIdNum);

  const [safetyConcerns, setSafetyConcerns] = useState<string[]>([]);
  const [initialSafetyConcerns, setInitialSafetyConcerns] = useState<string[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [modalState, setModalState] = useState<ModalState>(CLOSED_MODAL);
  const [entryPendingDelete, setEntryPendingDelete] = useState<DataTableRow | null>(null);

  const currentUserId: number | null = useSelector(getUserId) ?? null;
  const currentUserFirstName = useSelector((state: any) => state.user.first_name);
  const currentUserLastName = useSelector((state: any) => state.user.last_name);
  const currentUserName = [currentUserFirstName, currentUserLastName].filter(Boolean).join(" ");

  // Clinician+ (the API enforces the same rule, 403 otherwise).
  const currentUserIsClinician = useSelector(isClinician);
  // Supervisor+ only; the API enforces the same rule (403 otherwise).
  const currentUserIsSupervisor = useSelector(isSupervisor);
  const currentUserIsDirector = useSelector(isDirector);
  const currentUserIsAdmin = useSelector(isAdmin);
  const currentUserIsSuperAdmin = useSelector(isSuperAdmin);
  const isSupervisorPlus = Boolean(
    currentUserIsSupervisor || currentUserIsDirector || currentUserIsAdmin || currentUserIsSuperAdmin
  );
  const canDelete = isSupervisorPlus;
  const canEdit = Boolean(currentUserIsClinician || isSupervisorPlus);

  useEffect(() => {
    if (editMode) return;
    const concerns = clientData?.data?.safetyConcerns ?? [];
    setSafetyConcerns(concerns);
    setInitialSafetyConcerns(concerns);
  }, [clientData?.data, editMode]);

  const { updateClient: updateClientMutation, isUpdating } = useUpdateClient({
    onSuccess: () => {
      refetchClient();
      setEditMode(false);
    },
  });

  const closeModal = () => setModalState(CLOSED_MODAL);

  const { createAreaOfConcernMutation, isCreateAreaOfConcernLoading } = useCreateAreaOfConcern({
    onSuccess: () => {
      refetchAreasOfConcern();
      closeModal();
    },
  });

  const { updateAreaOfConcernMutation, isUpdateAreaOfConcernLoading } = useUpdateAreaOfConcern({
    onSuccess: () => {
      refetchAreasOfConcern();
      closeModal();
    },
  });

  const closeDeleteDialog = () => setEntryPendingDelete(null);

  const { deleteAreaOfConcernMutation, isDeleteAreaOfConcernLoading } = useDeleteAreaOfConcern({
    onSuccess: () => {
      refetchAreasOfConcern();
      closeDeleteDialog();
    },
  });

  const handleConfirmDelete = () => {
    if (!clientIdNum || !entryPendingDelete) return;
    deleteAreaOfConcernMutation({ clientId: clientIdNum, id: Number(entryPendingDelete.id) });
  };

  const handleSafetyConcernsChange = (value: string[]) => setSafetyConcerns(value);

  const handleSave = () => {
    if (!clientId) return;
    updateClientMutation({ clientId, data: { safetyConcerns } });
  };

  const handleCancel = () => {
    setSafetyConcerns(initialSafetyConcerns);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(safetyConcerns) === JSON.stringify(initialSafetyConcerns) || isUpdating;

  const entriesByCategory = (categoryKey: ConcernCategoryKey): AreaOfConcernEntry[] =>
    areasOfConcern.filter(
      (e) => ENUM_TO_CATEGORY[e.areaOfConcern] === categoryKey
    );

  const toRows = (entries: AreaOfConcernEntry[]): DataTableRow[] =>
    entries.map((entry) => ({
      id: String(entry.id),
      concernEntry: entry.concernEntry || "-",
      reportedDate: formatDate(entry.reportedDate),
      reportedByName: entry.reportedByName || "-",
    }));

  // Memoized by the selected entry (not rebuilt as a fresh object every render):
  // ConcernEntryModal resets its form whenever this prop's identity changes, and
  // isUpdateAreaOfConcernLoading toggling on a failed save re-renders this
  // component — an inline object literal here would wipe the user's in-progress
  // edit on that re-render even though nothing about the entry actually changed.
  const editingEntryInitialValues = useMemo(
    () =>
      modalState.editingEntry
        ? {
            concernEntry: modalState.editingEntry.concernEntry || "",
            reportedDate: modalState.editingEntry.reportedDate
              ? dateWithoutTimezone(modalState.editingEntry.reportedDate)
              : null,
          }
        : undefined,
    [modalState.editingEntry]
  );

  const handleModalSave = (
    formValues: { concernEntry: string; reportedDate: Date | null },
    assignReportedByToSelf: boolean
  ) => {
    if (!clientIdNum || !modalState.categoryKey) return;
    const basePayload = {
      concernEntry: formValues.concernEntry || null,
      reportedDate: formValues.reportedDate ? formValues.reportedDate.toISOString() : null,
    };

    if (modalState.editingEntry) {
      updateAreaOfConcernMutation({
        clientId: clientIdNum,
        id: modalState.editingEntry.id,
        payload: { ...basePayload, assignReportedByToSelf },
      });
    } else {
      createAreaOfConcernMutation({
        clientId: clientIdNum,
        payload: {
          ...basePayload,
          areaOfConcern: CONCERN_CATEGORIES_CONFIG[modalState.categoryKey].enumValue,
        },
      });
    }
  };

  if (!isAreasOfConcernFetched || !isClientFetched) {
    return (
      <SectionWrapper
        title="Areas of Concern"
        description="Track and categorize the client's areas requiring clinical attention and support."
        hideButtons
      >
        <Stack gap={2}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="text" width="50%" height={28} />
          <Skeleton variant="rounded" height={96} />
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Areas of Concern"
      description="Track and categorize the client's areas requiring clinical attention and support."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      <Grid container>
        {editMode ? (
          <LabeledSelect
            label="Safety Concerns"
            multiple
            value={safetyConcerns}
            onChange={handleSafetyConcernsChange}
            options={SAFETY_CONCERNS_OPTIONS}
            placeholder="Please select"
          />
        ) : (
          <LabeledText
            label="Safety Concerns"
            value={safetyConcerns
              .map((v) => SAFETY_CONCERNS_OPTIONS.find((o) => o.value === v)?.label ?? v)
              .join(", ")}
          />
        )}
      </Grid>

      {(Object.keys(CONCERN_CATEGORIES_CONFIG) as ConcernCategoryKey[]).map((categoryKey) => {
        const { label } = CONCERN_CATEGORIES_CONFIG[categoryKey];
        const entries = entriesByCategory(categoryKey);
        const rows = toRows(entries);
        const entryById = new Map(entries.map((entry) => [String(entry.id), entry]));

        return (
          <DataTable
            key={categoryKey}
            columns={getColumns(label, canEdit || canDelete)}
            rows={rows}
            ariaLabel={`${label} concerns`}
            emptyMessage={`No ${label.toLowerCase()} records`}
            onAddNew={() => setModalState({ open: true, categoryKey, editingEntry: null })}
            sx={{ mt: 7 }}
            renderCell={(col, row) =>
              col.key === "actions" ? (
                <RowOptions
                  onEdit={
                    canEdit
                      ? () =>
                          setModalState({
                            open: true,
                            categoryKey,
                            editingEntry: entryById.get(row.id) ?? null,
                          })
                      : undefined
                  }
                  onDelete={canDelete ? () => setEntryPendingDelete(row) : undefined}
                  editLabel={t("clientDetails:areasOfConcern.editRecord")}
                />
              ) : (
                (row[col.key] as string) || "-"
              )
            }
          />
        );
      })}

      <ConcernEntryModal
        open={modalState.open}
        categoryLabel={
          modalState.categoryKey ? CONCERN_CATEGORIES_CONFIG[modalState.categoryKey].label : ""
        }
        onClose={closeModal}
        onSave={handleModalSave}
        isSaving={isCreateAreaOfConcernLoading || isUpdateAreaOfConcernLoading}
        initialValues={editingEntryInitialValues}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        entryReportedById={modalState.editingEntry?.reportedBy ?? null}
        entryReportedByName={modalState.editingEntry?.reportedByName ?? null}
      />

      {canDelete && (
        <ConfirmDialog
          open={Boolean(entryPendingDelete)}
          title={t("clientDetails:areasOfConcern.deleteTitle")}
          confirmLabel={t("clientDetails:areasOfConcern.deleteConfirm")}
          confirmColor="error"
          onClose={closeDeleteDialog}
          onConfirm={handleConfirmDelete}
          isLoading={isDeleteAreaOfConcernLoading}
          maxWidth="xs"
        >
          <Alert severity="error">{t("clientDetails:areasOfConcern.deleteBody")}</Alert>
        </ConfirmDialog>
      )}
    </SectionWrapper>
  );
};

export default AreasOfConcern;
